<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilters from './components/ReportFilters.vue';
import reportsAPI from 'dashboard/api/reports';

const DEFAULT_SUMMARY = {
  total_campaigns: 0,
  total_recipients: 0,
  sent: 0,
  sending: 0,
  delivery_unknown: 0,
  opened: 0,
  clicked: 0,
  failed: 0,
  skipped: 0,
  pending: 0,
  processing: 0,
  not_received: 0,
  unsubscribed: 0,
  preference_updates: 0,
  skipped_unsubscribed: 0,
  skipped_no_consent: 0,
  open_rate: 0,
  click_rate: 0,
};

const { t, locale } = useI18n();

const from = ref(0);
const to = ref(0);
const status = ref('all');
const skipReason = ref('all');
const campaignId = ref('');
const search = ref('');
const page = ref(1);
const isLoading = ref(false);
const summary = ref({ ...DEFAULT_SUMMARY });
const campaigns = ref([]);
const deliveries = ref([]);
const filters = ref({
  statuses: [
    'all',
    'pending',
    'processing',
    'sending',
    'sent',
    'delivery_unknown',
    'skipped',
    'failed',
  ],
  skip_reasons: ['all'],
  campaigns: [],
});
const meta = ref({
  current_page: 1,
  page_size: 25,
  total_count: 0,
  total_pages: 0,
});

const formatPercent = value => `${Number(value || 0).toFixed(1)}%`;

const statusLabel = statusValue => {
  switch (statusValue) {
    case 'pending':
      return t('EMAIL_CAMPAIGN_REPORTS.STATUS.PENDING');
    case 'processing':
      return t('EMAIL_CAMPAIGN_REPORTS.STATUS.PROCESSING');
    case 'sending':
      return t('EMAIL_CAMPAIGN_REPORTS.STATUS.SENDING');
    case 'sent':
      return t('EMAIL_CAMPAIGN_REPORTS.STATUS.SENT');
    case 'delivery_unknown':
      return t('EMAIL_CAMPAIGN_REPORTS.STATUS.DELIVERY_UNKNOWN');
    case 'skipped':
      return t('EMAIL_CAMPAIGN_REPORTS.STATUS.SKIPPED');
    case 'failed':
      return t('EMAIL_CAMPAIGN_REPORTS.STATUS.FAILED');
    case 'all':
    default:
      return t('EMAIL_CAMPAIGN_REPORTS.STATUS.ALL');
  }
};

const skipReasonLabel = reason => {
  switch (reason) {
    case 'blank_email':
      return t('EMAIL_CAMPAIGN_REPORTS.SKIP_REASON.BLANK_EMAIL');
    case 'unsubscribed':
      return t('EMAIL_CAMPAIGN_REPORTS.SKIP_REASON.UNSUBSCRIBED');
    case 'no_consent':
      return t('EMAIL_CAMPAIGN_REPORTS.SKIP_REASON.NO_CONSENT');
    case 'blocked_contact':
      return t('EMAIL_CAMPAIGN_REPORTS.SKIP_REASON.BLOCKED_CONTACT');
    case 'all':
    default:
      return t('EMAIL_CAMPAIGN_REPORTS.SKIP_REASON.ALL');
  }
};

const requestPayload = computed(() => ({
  from: from.value,
  to: to.value,
  status: status.value,
  skipReason: skipReason.value,
  campaignId: campaignId.value,
  search: search.value,
  page: page.value,
}));

const statusOptions = computed(() =>
  filters.value.statuses.map(statusValue => ({
    value: statusValue,
    label: statusLabel(statusValue),
  }))
);

const skipReasonOptions = computed(() =>
  filters.value.skip_reasons.map(reason => ({
    value: reason,
    label: skipReasonLabel(reason),
  }))
);

const campaignOptions = computed(() => [
  {
    id: '',
    title: t('EMAIL_CAMPAIGN_REPORTS.FILTERS.ALL_CAMPAIGNS'),
  },
  ...filters.value.campaigns,
]);

const canGoPrevious = computed(() => Number(meta.value.current_page) > 1);

const canGoNext = computed(
  () => Number(meta.value.current_page) < Number(meta.value.total_pages)
);

const rangeLabel = computed(() => {
  if (!meta.value.total_count) {
    return t('EMAIL_CAMPAIGN_REPORTS.TABLE.EMPTY');
  }

  const first =
    (Number(meta.value.current_page) - 1) * Number(meta.value.page_size) + 1;
  const last = Math.min(
    Number(meta.value.total_count),
    Number(meta.value.current_page) * Number(meta.value.page_size)
  );

  return t('EMAIL_CAMPAIGN_REPORTS.TABLE.RANGE', {
    first,
    last,
    total: meta.value.total_count,
  });
});

const pageCounter = computed(
  () => `${meta.value.current_page} / ${meta.value.total_pages || 1}`
);

const metricCards = computed(() => [
  [
    t('EMAIL_CAMPAIGN_REPORTS.METRICS.TOTAL_CAMPAIGNS'),
    summary.value.total_campaigns,
  ],
  [
    t('EMAIL_CAMPAIGN_REPORTS.METRICS.TOTAL_RECIPIENTS'),
    summary.value.total_recipients,
  ],
  [t('EMAIL_CAMPAIGN_REPORTS.METRICS.SENT'), summary.value.sent],
  [t('EMAIL_CAMPAIGN_REPORTS.METRICS.SENDING'), summary.value.sending],
  [
    t('EMAIL_CAMPAIGN_REPORTS.METRICS.DELIVERY_UNKNOWN'),
    summary.value.delivery_unknown,
  ],
  [
    t('EMAIL_CAMPAIGN_REPORTS.METRICS.NOT_RECEIVED'),
    summary.value.not_received,
  ],
  [
    t('EMAIL_CAMPAIGN_REPORTS.METRICS.UNSUBSCRIBED'),
    summary.value.unsubscribed,
  ],
  [
    t('EMAIL_CAMPAIGN_REPORTS.METRICS.PREFERENCE_UPDATES'),
    summary.value.preference_updates,
  ],
  [
    t('EMAIL_CAMPAIGN_REPORTS.METRICS.SKIPPED_NO_CONSENT'),
    summary.value.skipped_no_consent,
  ],
  [
    t('EMAIL_CAMPAIGN_REPORTS.METRICS.SKIPPED_UNSUBSCRIBED'),
    summary.value.skipped_unsubscribed,
  ],
  [
    t('EMAIL_CAMPAIGN_REPORTS.METRICS.OPENED'),
    `${summary.value.opened} (${formatPercent(summary.value.open_rate)})`,
  ],
  [
    t('EMAIL_CAMPAIGN_REPORTS.METRICS.CLICKED'),
    `${summary.value.clicked} (${formatPercent(summary.value.click_rate)})`,
  ],
]);

const fetchReports = () => {
  isLoading.value = true;
  reportsAPI
    .getCampaignReports(requestPayload.value)
    .then(({ data }) => {
      summary.value = { ...DEFAULT_SUMMARY, ...(data.summary || {}) };
      campaigns.value = data.campaigns || [];
      deliveries.value = data.deliveries || [];
      filters.value = data.filters || filters.value;
      meta.value = data.meta || meta.value;
    })
    .catch(() => {
      useAlert(t('EMAIL_CAMPAIGN_REPORTS.ERROR'));
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const onFilterChange = ({ from: filterFrom, to: filterTo }) => {
  from.value = filterFrom;
  to.value = filterTo;
  page.value = 1;
  fetchReports();
};

const onDenseFilterChange = () => {
  page.value = 1;
  fetchReports();
};

const onSearch = () => {
  page.value = 1;
  fetchReports();
};

const goToPage = targetPage => {
  if (targetPage < 1 || targetPage > Number(meta.value.total_pages)) return;

  page.value = targetPage;
  fetchReports();
};

const emptyValue = () => t('EMAIL_CAMPAIGN_REPORTS.TABLE.EMPTY_VALUE');

const formatDate = timestamp => {
  if (!timestamp) return emptyValue();

  return new Intl.DateTimeFormat((locale.value || 'en').replace('_', '-'), {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(timestamp * 1000));
};

const sentEngagementValue = (delivery, key) =>
  delivery.status === 'sent' ? delivery[key] : null;

const contactName = delivery =>
  delivery.contact?.name || delivery.email || emptyValue();

const contactEmail = delivery =>
  delivery.email || delivery.contact?.email || emptyValue();

const deliveryProblem = delivery => {
  if (delivery.status === 'sent') return '-';

  return delivery.error_message || t('EMAIL_CAMPAIGN_REPORTS.TABLE.NOT_SENT');
};

const statusClass = statusValue => {
  const statusClasses = {
    sent: 'bg-n-teal-3 text-n-teal-11',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
    skipped: 'bg-n-amber-3 text-n-amber-11',
    pending: 'bg-n-slate-3 text-n-slate-11',
    processing: 'bg-n-blue-3 text-n-blue-11',
    sending: 'bg-n-blue-3 text-n-blue-11',
    delivery_unknown: 'bg-n-amber-3 text-n-amber-11',
  };

  return statusClasses[statusValue] || statusClasses.pending;
};

const campaignReference = delivery =>
  `#${delivery.campaign?.id || emptyValue()}`;
</script>

<template>
  <ReportHeader
    :header-title="t('EMAIL_CAMPAIGN_REPORTS.HEADER')"
    :header-description="t('EMAIL_CAMPAIGN_REPORTS.DESCRIPTION')"
  />

  <div class="flex flex-col gap-5">
    <ReportFilters
      :show-entity-filter="false"
      :show-group-by="false"
      :show-business-hours="false"
      @filter-change="onFilterChange"
    />

    <div class="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
      <div
        v-for="[label, value] in metricCards"
        :key="label"
        class="px-4 py-3 rounded-lg bg-n-solid-2 outline outline-1 outline-n-container"
      >
        <div class="text-xs font-medium uppercase text-n-slate-10">
          {{ label }}
        </div>
        <div class="mt-1 text-xl font-semibold text-n-slate-12">
          {{ value }}
        </div>
      </div>
    </div>

    <div
      class="flex flex-col gap-3 p-4 rounded-xl bg-n-solid-2 outline outline-1 outline-n-container lg:flex-row lg:items-end"
    >
      <label class="flex flex-col gap-1 text-sm text-n-slate-11 lg:w-64">
        {{ t('EMAIL_CAMPAIGN_REPORTS.FILTERS.CAMPAIGN') }}
        <select
          v-model="campaignId"
          class="h-10 px-3 rounded-md border border-n-weak bg-n-alpha-2 text-n-slate-12"
          @change="onDenseFilterChange"
        >
          <option
            v-for="campaign in campaignOptions"
            :key="campaign.id || 'all'"
            :value="campaign.id"
          >
            {{ campaign.title }}
          </option>
        </select>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-11 lg:w-48">
        {{ t('EMAIL_CAMPAIGN_REPORTS.FILTERS.STATUS') }}
        <select
          v-model="status"
          class="h-10 px-3 rounded-md border border-n-weak bg-n-alpha-2 text-n-slate-12"
          @change="onDenseFilterChange"
        >
          <option
            v-for="statusOption in statusOptions"
            :key="statusOption.value"
            :value="statusOption.value"
          >
            {{ statusOption.label }}
          </option>
        </select>
      </label>

      <label class="flex flex-col gap-1 text-sm text-n-slate-11 lg:w-56">
        {{ t('EMAIL_CAMPAIGN_REPORTS.FILTERS.SKIP_REASON') }}
        <select
          v-model="skipReason"
          class="h-10 px-3 rounded-md border border-n-weak bg-n-alpha-2 text-n-slate-12"
          @change="onDenseFilterChange"
        >
          <option
            v-for="reasonOption in skipReasonOptions"
            :key="reasonOption.value"
            :value="reasonOption.value"
          >
            {{ reasonOption.label }}
          </option>
        </select>
      </label>

      <label class="flex flex-col flex-1 gap-1 text-sm text-n-slate-11">
        {{ t('EMAIL_CAMPAIGN_REPORTS.FILTERS.SEARCH') }}
        <input
          v-model="search"
          type="search"
          class="h-10 px-3 rounded-md border border-n-weak bg-n-alpha-2 text-n-slate-12"
          :placeholder="t('EMAIL_CAMPAIGN_REPORTS.FILTERS.SEARCH_PLACEHOLDER')"
          @keyup.enter="onSearch"
        />
      </label>

      <V4Button
        :label="t('EMAIL_CAMPAIGN_REPORTS.FILTERS.APPLY')"
        icon="i-ph-magnifying-glass"
        size="sm"
        @click="onSearch"
      />
    </div>

    <div
      class="rounded-xl bg-n-solid-2 outline outline-1 outline-n-container overflow-hidden"
    >
      <div class="overflow-x-auto">
        <table class="min-w-[1180px] w-full text-sm">
          <thead class="bg-n-slate-2 border-b border-n-container">
            <tr>
              <th class="px-4 py-3 text-left font-medium text-n-slate-11">
                {{ t('EMAIL_CAMPAIGN_REPORTS.TABLE.CAMPAIGN') }}
              </th>
              <th class="px-4 py-3 text-left font-medium text-n-slate-11">
                {{ t('EMAIL_CAMPAIGN_REPORTS.TABLE.CONTACT') }}
              </th>
              <th class="px-4 py-3 text-left font-medium text-n-slate-11">
                {{ t('EMAIL_CAMPAIGN_REPORTS.TABLE.EMAIL') }}
              </th>
              <th class="px-4 py-3 text-left font-medium text-n-slate-11">
                {{ t('EMAIL_CAMPAIGN_REPORTS.TABLE.STATUS') }}
              </th>
              <th class="px-4 py-3 text-left font-medium text-n-slate-11">
                {{ t('EMAIL_CAMPAIGN_REPORTS.TABLE.SENT_AT') }}
              </th>
              <th class="px-4 py-3 text-left font-medium text-n-slate-11">
                {{ t('EMAIL_CAMPAIGN_REPORTS.TABLE.OPENED') }}
              </th>
              <th class="px-4 py-3 text-left font-medium text-n-slate-11">
                {{ t('EMAIL_CAMPAIGN_REPORTS.TABLE.CLICKED') }}
              </th>
              <th class="px-4 py-3 text-left font-medium text-n-slate-11">
                {{ t('EMAIL_CAMPAIGN_REPORTS.TABLE.LAST_CLICK') }}
              </th>
              <th class="px-4 py-3 text-left font-medium text-n-slate-11">
                {{ t('EMAIL_CAMPAIGN_REPORTS.TABLE.PROBLEM') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-container">
            <tr v-if="isLoading">
              <td colspan="9" class="px-4 py-10 text-center text-n-slate-11">
                {{ t('EMAIL_CAMPAIGN_REPORTS.LOADING') }}
              </td>
            </tr>
            <tr v-else-if="!deliveries.length">
              <td colspan="9" class="px-4 py-10 text-center text-n-slate-11">
                {{ t('EMAIL_CAMPAIGN_REPORTS.TABLE.EMPTY') }}
              </td>
            </tr>
            <template v-else>
              <tr
                v-for="delivery in deliveries"
                :key="delivery.id"
                class="hover:bg-n-slate-2"
              >
                <td class="px-4 py-3 text-n-slate-12">
                  <div class="font-medium truncate max-w-52">
                    {{ delivery.campaign?.title || '-' }}
                  </div>
                  <div class="text-xs text-n-slate-10">
                    {{ campaignReference(delivery) }}
                  </div>
                </td>
                <td class="px-4 py-3 text-n-slate-12">
                  {{ contactName(delivery) }}
                </td>
                <td class="px-4 py-3 text-n-slate-11">
                  {{ contactEmail(delivery) }}
                </td>
                <td class="px-4 py-3">
                  <span
                    class="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md"
                    :class="statusClass(delivery.status)"
                  >
                    {{ statusLabel(delivery.status) }}
                  </span>
                </td>
                <td class="px-4 py-3 text-n-slate-11">
                  {{ formatDate(delivery.sent_at) }}
                </td>
                <td class="px-4 py-3 text-n-slate-11">
                  <div>
                    {{ sentEngagementValue(delivery, 'open_count') || 0 }}
                  </div>
                  <div class="text-xs text-n-slate-10">
                    {{ formatDate(sentEngagementValue(delivery, 'opened_at')) }}
                  </div>
                </td>
                <td class="px-4 py-3 text-n-slate-11">
                  <div>
                    {{ sentEngagementValue(delivery, 'click_count') || 0 }}
                  </div>
                  <div class="text-xs text-n-slate-10">
                    {{
                      formatDate(sentEngagementValue(delivery, 'clicked_at'))
                    }}
                  </div>
                </td>
                <td class="px-4 py-3 text-n-slate-11">
                  <a
                    v-if="sentEngagementValue(delivery, 'last_clicked_url')"
                    :href="sentEngagementValue(delivery, 'last_clicked_url')"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-block max-w-56 truncate text-n-brand"
                  >
                    {{ sentEngagementValue(delivery, 'last_clicked_url') }}
                  </a>
                  <span v-else>{{ emptyValue() }}</span>
                </td>
                <td class="px-4 py-3 text-n-slate-11">
                  <span class="inline-block max-w-64 truncate">
                    {{ deliveryProblem(delivery) }}
                  </span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div
        class="flex items-center justify-between gap-4 px-4 py-3 border-t border-n-container"
      >
        <span class="text-sm text-n-slate-11">
          {{ rangeLabel }}
        </span>
        <div class="flex items-center gap-2">
          <V4Button
            :label="t('EMAIL_CAMPAIGN_REPORTS.PAGINATION.PREVIOUS')"
            size="sm"
            variant="outline"
            color="slate"
            :disabled="!canGoPrevious"
            @click="goToPage(Number(meta.current_page) - 1)"
          />
          <span class="text-sm text-n-slate-11">
            {{ pageCounter }}
          </span>
          <V4Button
            :label="t('EMAIL_CAMPAIGN_REPORTS.PAGINATION.NEXT')"
            size="sm"
            variant="outline"
            color="slate"
            :disabled="!canGoNext"
            @click="goToPage(Number(meta.current_page) + 1)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
